"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

const hrefOf = (url) => (/^https?:\/\//.test(url) ? url : `https://${url}`);
const domainOf = (url) => {
    try {
        return new URL(hrefOf(url)).hostname.replace(/^www\./, "");
    } catch {
        return url;
    }
};

// L'onglet « Projets » d'un profil : une carte par projet, remplie avec l'image, le titre et la
// description que la page du projet annonce elle-même (helpers/profileProjects.js).
// Sur son propre profil (`own`), le membre ajoute un projet en collant son adresse et peut en
// retirer un : `onAdd(adresse)` et `onRemove(adresse)` rendent une promesse.
export default function ProfileProjects({ projects = [], own = false, onAdd, onRemove, className = "" }) {
    const t = useTranslations();
    const [address, setAddress] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (event) => {
        event.preventDefault();
        const value = address.trim();
        if (!value || busy) return;
        setBusy(true);
        const added = await onAdd(value);
        setBusy(false);
        if (added) setAddress("");
    };

    return (
        <div className={className}>
            {own && (
                <form onSubmit={submit} className="mb-6 rounded-2xl bg-gray-50 p-4 dark:bg-white/5">
                    <label htmlFor="profile-project-address" className="mb-1 block text-sm font-semibold text-gray-900 dark:text-white">
                        {t("account_projects_add")}
                    </label>
                    <p className="mb-3 text-sm text-gray-500 dark:text-textSecondDark">{t("profile_projects_add_hint")}</p>
                    <div className="flex flex-col gap-2 sm:flex-row">
                        <input
                            id="profile-project-address"
                            type="text"
                            inputMode="url"
                            autoComplete="url"
                            value={address}
                            onChange={(event) => setAddress(event.target.value)}
                            placeholder="https://"
                            className="min-w-0 flex-1 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-900 outline-none transition-colors focus:border-primary dark:border-borderDark dark:bg-bgDark-800 dark:text-white"
                        />
                        <button type="submit" disabled={busy || !address.trim()} className="flex items-center justify-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-white transition-opacity disabled:opacity-50">
                            <SolarIcon name={busy ? "refresh-linear" : "add-circle-linear"} className={`text-lg ${busy ? "animate-spin" : ""}`} />
                            {t("profile_projects_add_button")}
                        </button>
                    </div>
                </form>
            )}

            {projects.length === 0 ? (
                <p className="mb-0 text-gray-500 dark:text-textSecondDark">{t(own ? "profile_projects_owner_empty" : "profile_projects_empty")}</p>
            ) : (
                <div className="grid gap-4 sm:grid-cols-2">
                    {projects.map((project) => {
                        const domain = domainOf(project.requestUrl);
                        const title = project.ogTitle || domain;
                        return (
                            <div key={project.requestUrl} className="group relative">
                                <a
                                    href={hrefOf(project.requestUrl)}
                                    target="_blank"
                                    rel="noopener noreferrer nofollow ugc"
                                    className="flex h-full flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white transition-[transform,box-shadow] duration-200 ease-out hover:-translate-y-0.5 hover:shadow-lg dark:border-borderDark dark:bg-bgDark-800"
                                >
                                    <div className="aspect-[1200/630] overflow-hidden bg-gray-100 dark:bg-white/5">
                                        {project.ogImage?.url ? (
                                            // eslint-disable-next-line @next/next/no-img-element
                                            <img src={project.ogImage.url} alt="" loading="lazy" referrerPolicy="no-referrer" className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.03]" />
                                        ) : (
                                            // Pas d'image annoncée par la page : l'initiale du site, pour que la carte ne soit pas vide.
                                            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-violet-100 to-sky-100 text-5xl font-extrabold uppercase text-violet-400 dark:from-violet-500/20 dark:to-sky-500/20 dark:text-violet-300">
                                                {domain.charAt(0)}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex flex-1 flex-col p-4">
                                        <p className="mb-1 line-clamp-2 text-base font-semibold leading-snug text-gray-900 dark:text-white">{title}</p>
                                        {project.ogDescription && <p className="mb-0 line-clamp-2 text-sm text-gray-600 dark:text-textSecondDark">{project.ogDescription}</p>}
                                        <span className="mt-3 flex items-center gap-1.5 text-xs font-medium text-gray-500 group-hover:text-primary dark:text-gray-400">
                                            <SolarIcon name="link-minimalistic-2-linear" className="text-sm" />
                                            <span className="truncate">{domain}</span>
                                        </span>
                                    </div>
                                </a>
                                {own && (
                                    <button
                                        type="button"
                                        onClick={() => onRemove(project.requestUrl)}
                                        aria-label={t("profile_projects_remove")}
                                        title={t("profile_projects_remove")}
                                        className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-xl bg-white/90 text-gray-700 shadow-sm backdrop-blur transition-colors hover:text-red-600"
                                    >
                                        <SolarIcon name="trash-bin-minimalistic-linear" className="text-lg" />
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
